import React, { useState, useCallback, useMemo } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LayoutDashboard, BookOpen, Crown, ShoppingBag, Receipt, Sparkles, Bell, LogOut, Menu, X, User, Eye, Megaphone, GraduationCap, School } from "lucide-react";
import Brand from "../Brand";
import { useAuth } from "../../contexts/AuthContext";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import useCachedFetch from "../../hooks/useCachedFetch";
import ChatWidget from "../student/ChatWidget";
import NotificationPopup from "../student/NotificationPopup";
import "../../styles/student.css";

const NAV = [
  { to: "/student/dashboard",     label: "Dashboard",        icon: LayoutDashboard },
  { to: "/student/classroom",     label: "Classroom",        icon: BookOpen },
  // { to: "/student/subscription",  label: "Subscription",     icon: Crown },
  { to: "/student/shop",          label: "Shop",             icon: ShoppingBag },
  { to: "/student/order",         label: "Order",            icon: Receipt },
  { to: "/student/fun-hub",       label: "Fun Learning Hub", icon: Sparkles },
];

export default function StudentSiteLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [popupNotif, setPopupNotif] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);

  const { data: notifData } = useCachedFetch("/student/site/notifications", 20);
  const bellNotifs = useMemo(() => notifData?.notifications || [], [notifData]);
  const [unreadCount, setUnreadCount] = useState(() => bellNotifs.filter((n) => !n.read).length);
  React.useEffect(() => { setUnreadCount(bellNotifs.filter((n) => !n.read).length); }, [bellNotifs]);

  const openNotifDetail = useCallback(async (n) => {
    setBellOpen(false);
    setPopupNotif(n);
    if (!n.read) {
      try { await api.post(`/student/site/notifications/${n.id}/read`); } catch {}
      setUnreadCount((c) => Math.max(0, c - 1));
    }
  }, []);

  const initials = (user?.name || "S").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="student-site" data-testid="student-site">
      {/* Top Navigation */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#e3eeee]">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-4 md:gap-6">
          <button className="md:hidden p-2 -ml-1" onClick={() => setOpen(true)} aria-label="Open menu" data-testid="student-burger">
            <Menu size={20} />
          </button>
          <Brand theme="light" />
          <nav className="hidden md:flex items-center gap-1 ml-4">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink key={n.to} to={n.to} data-testid={`student-nav-${n.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={({ isActive }) => [
                    "px-3.5 py-2 rounded-full text-sm font-medium inline-flex items-center gap-2 transition",
                    isActive ? "bg-[var(--cms-teal)] text-white shadow-[0_6px_18px_rgba(18,107,110,0.25)]" : "text-[var(--cms-teal-deep)] hover:bg-[var(--cms-teal-soft)]",
                  ].join(" ")}>
                  <Icon size={14} /> {n.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => setBellOpen((v) => !v)}
              className="relative w-9 h-9 rounded-full flex items-center justify-center hover:bg-[var(--cms-teal-soft)] transition"
              aria-label="Notifications"
            >
              <Bell size={18} className="text-[var(--cms-teal-deep)]" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-red-500 rounded-full text-white text-[9px] font-bold flex items-center justify-center border-2 border-white min-w-[18px] min-h-[18px] leading-none">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {/* Bell notifications list popup */}
            <AnimatePresence>
              {bellOpen && (
                <>
                  <motion.div
                    className="fixed inset-0 z-50"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={() => setBellOpen(false)}
                  />
                  <motion.div
                    className="absolute top-full right-0 mt-2 z-50 bg-white rounded-2xl shadow-2xl border border-[#e3eeee] w-80 sm:w-96 max-h-[70vh] overflow-y-auto"
                    initial={{ opacity: 0, scale: 0.95, y: -8 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -8 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                  >
                    <div className="sticky top-0 bg-white z-10 flex items-center justify-between px-4 py-3 border-b border-[#e3eeee] rounded-t-2xl">
                      <p className="text-xs uppercase tracking-widest text-[var(--cms-muted)] font-semibold">Notifications</p>
                      <button onClick={() => setBellOpen(false)} className="text-zinc-400 hover:text-zinc-600"><X size={14} /></button>
                    </div>
                    {bellNotifs.length === 0 && (
                      <div className="p-6 text-center text-sm text-[var(--cms-muted)]">Nothing here yet.</div>
                    )}
                    {bellNotifs.slice(0, 10).map((n) => {
                      const cfg = ({ all: { Icon: Megaphone, tint: "from-[#fbd86b] to-[#f59849]" }, school: { Icon: School, tint: "from-[#1c8e8a] to-[#0d3b3f]" }, class: { Icon: GraduationCap, tint: "from-[#4d80f4] to-[#2a4ea8]" }, info: { Icon: Bell, tint: "from-[#d23028] to-[#a31f1a]" } })[n.audience || "info"] || {};
                      const Icon = cfg.Icon || Bell;
                      return (
                        <button
                          key={n.id}
                          onClick={() => openNotifDetail(n)}
                          className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-[var(--cms-teal-soft)]/40 transition border-b border-[#e3eeee] last:border-b-0"
                        >
                          <div className={`w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br ${cfg.tint} grid place-items-center text-white relative`}>
                            <Icon size={14} />
                            {!n.read && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm truncate ${n.read ? "font-medium text-[var(--cms-teal-deep)]" : "font-bold text-[var(--cms-teal-deep)]"}`}>{n.title}</p>
                            {n.body && <p className="text-xs text-[var(--cms-muted)] truncate mt-0.5">{n.body}</p>}
                          </div>
                        </button>
                      );
                    })}
                    {bellNotifs.length > 0 && (
                      <div className="p-3 text-center border-t border-[#e3eeee]">
                        <button onClick={() => { setBellOpen(false); navigate("/student/notifications"); }} className="text-xs text-[var(--cms-teal)] font-semibold inline-flex items-center gap-1 hover:underline">
                          <Eye size={12} /> View all {bellNotifs.length} notifications
                        </button>
                      </div>
                    )}
                  </motion.div>
                </>
              )}
            </AnimatePresence>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 pl-1.5 pr-1.5 py-1 rounded-full hover:bg-[var(--cms-teal-soft)] transition" data-testid="student-profile-trigger">
                  <div className="w-10 h-10 rounded-full bg-[var(--cms-teal)] text-white flex items-center justify-center font-semibold text-sm shadow-md ring-2 ring-[var(--cms-yellow)] overflow-hidden" data-testid="student-avatar">
                    {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : initials}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="!bg-white !border !border-[#e3eeee] !text-[#0d3b3f] min-w-[220px] shadow-lg">
                <DropdownMenuLabel className="text-xs text-[var(--cms-muted)] truncate max-w-[200px] font-normal">{user?.email || "Student"}</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-[#e3eeee]" />
                <DropdownMenuItem onClick={() => navigate("/student/profile")} data-testid="student-menu-profile" className="cursor-pointer text-[#0d3b3f] focus:bg-[var(--cms-teal-soft)] focus:text-[#0d3b3f]">
                  <User size={14} className="mr-2" /> My Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#e3eeee]" />
                <DropdownMenuItem
                  onClick={async () => { await logout(); toast.success("See you soon!"); navigate("/login", { replace: true }); }}
                  data-testid="student-menu-logout"
                  className="text-[var(--cms-red)] focus:text-[var(--cms-red)] focus:bg-red-50 cursor-pointer"
                >
                  <LogOut size={14} className="mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 bg-black/40 z-50 md:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ type: "spring", damping: 30 }} className="fixed inset-y-0 left-0 z-50 w-72 bg-white md:hidden p-5 space-y-3">
              <div className="flex items-center justify-between mb-4">
                <Brand theme="light" />
                <button onClick={() => setOpen(false)} className="p-2" aria-label="Close menu"><X size={18} /></button>
              </div>
              {NAV.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                    className={({ isActive }) => `block px-3 py-2.5 rounded-xl text-sm font-medium ${isActive ? "bg-[var(--cms-teal)] text-white" : "text-[var(--cms-teal-deep)] hover:bg-[var(--cms-teal-soft)]"}`}>
                    <Icon size={14} className="inline mr-2" />{n.label}
                  </NavLink>
                );
              })}
              <button onClick={async () => { await logout(); navigate("/login"); }} className="block w-full text-left mt-4 px-3 py-2.5 rounded-xl text-sm text-[var(--cms-red)] hover:bg-red-50">
                <LogOut size={14} className="inline mr-2" />Sign out
              </button>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        <AnimatePresence mode="popLayout">
          <motion.div key={location.pathname} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}>
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Floating AI study buddy */}
      <ChatWidget context={location.pathname.startsWith("/student/classroom") ? "the student is inside a classroom lesson" : ""} />
      <NotificationPopup notification={popupNotif} onClose={() => setPopupNotif(null)} />
    </div>
  );
}
